import { Close } from '@mui/icons-material';
import clsx from 'clsx';
import React from 'react';

export type BannerProps = {
	title?: string;
	message: string | React.ReactNode;
	status?: 'default' | 'info' | 'warning' | 'error';
	action?: {
		label: string;
		onClick: () => void;
	};
	onClose?: () => void;
	className?: string;
};

export function Banner({
	title,
	message,
	status = 'default',
	onClose,
	className,
	children,
}: React.PropsWithChildren<BannerProps>) {
	return (
		<div
			className={clsx(
				'relative flex gap-2 rounded-md p-4 text-sm justify-start',
				{
					'bg-grey-30 border border-borders-secondary': status === 'default',
					'bg-success-blue-5 border border-borders-info': status === 'info',
					'bg-warning-yellow-5 border border-borders-warning': status === 'warning',
					'bg-failure-red-5 border border-borders-error': status === 'error',
				},
				{
					'text-text-primary': status === 'default',
					'text-text-brand_default': status === 'info',
					'text-text-warning': status === 'warning',
					'text-text-error': status === 'error',
				},
				className,
			)}
		>
			<div className="flex flex-col gap-2 grow">
				{title && (
					<div className={clsx(
						'font-medium',
						{
							'text-text-primary': status === 'default',
							'text-text-brand_default': status === 'info',
							'text-text-warning': status === 'warning',
							'text-text-error': status === 'error',
						},
					)}>
						{title}
					</div>
				)}
				<div className="grow">{children ?? message}</div>
			</div>
			{onClose && (
				<button
					className={clsx(
						'h-6 w-6 rounded-md',
						{
							'hover:bg-grey-100': status === 'default',
							'hover:bg-night-blue-100': status === 'info',
							'hover:bg-yellow-100': status === 'warning',
							'hover:bg-red-100': status === 'error',
						},
						className,
					)}
					onClick={onClose}
				>
					<Close className="size-5 text-inherit opacity-40 hover:opacity-100 isolate" />
				</button>
			)}
		</div>
	);
}
